// Independent Quick Add for Product Page Featured Collection Row
// This script completely bypasses theme.js's quickAdd logic to avoid conflicts

// IMMEDIATE LOG - This should appear even if script fails later
try {
  console.log('[FCR Quick Add] ✅ Script file loaded and executing');
  console.log('[FCR Quick Add] Script location:', window.location.href);
  console.log('[FCR Quick Add] Document readyState:', document.readyState);
  console.log('[FCR Quick Add] Document body exists:', !!document.body);
} catch(e) {
  console.error('[FCR Quick Add] ❌ Error in initial logging:', e);
}

const FCR_DEBUG = {
  enabled: false,
  log: function(...args) {
    if (this.enabled) {
      console.log('[FCR Quick Add]', ...args);
    }
  },
  error: function(...args) {
    if (this.enabled) {
      console.error('[FCR Quick Add]', ...args);
    }
  },
  warn: function(...args) {
    if (this.enabled) {
      console.warn('[FCR Quick Add]', ...args);
    }
  }
};

FCR_DEBUG.log('FCR_DEBUG object created');

// Store target variant info
let targetVariantId = null;
let targetProductHandle = null;
let targetColorValue = null;

// Cache DOM elements
let cachedElements = {
  modal: null,
  modalContent: null,
  mainImage: null,
  variantContainer: null,
  variantJson: null
};

// Track if event listeners are already set up
let eventListenersSetup = false;

// Get routes from theme object
function getProductRoute() {
  return window.theme?.routes?.products || '/products';
}

// Make AJAX request
function makeRequest(method, url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.responseText);
      } else {
        reject(new Error('Request failed with status: ' + xhr.status));
      }
    };
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    xhr.send();
  });
}

// Open modal - matches theme.js quickAddModal _open() implementation
function openModal() {
  const modal = document.querySelector('[data-quick-add-modal]');
  if (!modal) {
    FCR_DEBUG.error('Modal element not found');
    return;
  }
  
  cachedElements.modal = modal;
  
  FCR_DEBUG.log('Opening modal...');
  FCR_DEBUG.log('Modal element:', modal);
  FCR_DEBUG.log('Modal classes before:', modal.className);
  
  // Step 1: Add is-fixed class immediately (for positioning)
  modal.classList.add('is-fixed');
  FCR_DEBUG.log('Added is-fixed class');
  
  // Step 2: After 50ms, add is-visible and active classes (for animation)
  setTimeout(() => {
    modal.classList.add('is-visible');
    modal.classList.add('active');
    FCR_DEBUG.log('Added is-visible and active classes');
    FCR_DEBUG.log('Modal classes after:', modal.className);
    
    // Set aria-hidden to false
    modal.setAttribute('aria-hidden', 'false');
    
    // Focus trap - focus on close button
    const closeBtn = modal.querySelector('[data-quick-add-modal-close]');
    if (closeBtn) {
      closeBtn.focus();
      FCR_DEBUG.log('Focused close button');
    }
    
    // Disable body scroll (if focus trap library is available)
    if (typeof disableBodyScroll !== 'undefined') {
      try {
        disableBodyScroll(modal, {
          allowTouchMove: el => {
            while (el && el !== document.body) {
              if (el.getAttribute('data-scroll-lock-ignore') !== null) {
                return true;
              }
              el = el.parentNode;
            }
          },
          reserveScrollBarGap: true
        });
        FCR_DEBUG.log('Body scroll disabled');
      } catch (e) {
        FCR_DEBUG.warn('Could not disable body scroll:', e);
        // Fallback: add modal-open class to body
        document.body.classList.add('modal-open');
      }
    } else {
      // Fallback: add modal-open class to body
      document.body.classList.add('modal-open');
      FCR_DEBUG.log('Added modal-open class to body (fallback)');
    }
    
    FCR_DEBUG.log('Modal fully opened');
  }, 50);
}

// Close modal - matches theme.js quickAddModal _close() implementation
function closeModal() {
  if (!cachedElements.modal) return;
  
  FCR_DEBUG.log('Closing modal...');
  
  // Remove visible and active classes immediately
  cachedElements.modal.classList.remove('is-visible');
  cachedElements.modal.classList.remove('active');
  
  // Set aria-hidden to true
  cachedElements.modal.setAttribute('aria-hidden', 'true');
  
  // Enable body scroll (if enableBodyScroll is available)
  if (typeof enableBodyScroll !== 'undefined') {
    try {
      enableBodyScroll(cachedElements.modal);
      FCR_DEBUG.log('Body scroll enabled');
    } catch (e) {
      FCR_DEBUG.warn('Could not enable body scroll:', e);
      // Fallback: remove modal-open class from body
      document.body.classList.remove('modal-open');
    }
  } else {
    // Fallback: remove modal-open class from body
    document.body.classList.remove('modal-open');
    FCR_DEBUG.log('Removed modal-open class from body (fallback)');
  }
  
  // After 300ms (animation duration), remove is-fixed class and clear content
  setTimeout(() => {
    cachedElements.modal.classList.remove('is-fixed');
    
    // Clear content
    if (cachedElements.modalContent) {
      cachedElements.modalContent.innerHTML = '';
      FCR_DEBUG.log('Modal content cleared');
    }
    
    // Reset cached elements
    cachedElements.mainImage = null;
    cachedElements.variantContainer = null;
    cachedElements.variantJson = null;
    targetVariantId = null;
    targetProductHandle = null;
    targetColorValue = null;
    
    FCR_DEBUG.log('Modal fully closed');
  }, 300);
}

// Load product content
async function loadProductContent(handle) {
  try {
    const requestUrl = `${getProductRoute()}/${encodeURIComponent(handle)}?section_id=quick-add-product`;
    FCR_DEBUG.log('Loading product content from:', requestUrl);
    
    const response = await makeRequest('GET', requestUrl);
    FCR_DEBUG.log('Response received, length:', response.length);
    
    // Shopify section response might be wrapped in HTML or just the section content
    // Try to find the product element in the response
    let productElement = null;
    
    // Try parsing as full HTML document first
    const parser = new DOMParser();
    const doc = parser.parseFromString(response, 'text/html');
    
    // Look for the product element
    productElement = doc.querySelector('[data-quick-add-product]');
    
    // If not found, try finding it in the body or directly in response
    if (!productElement) {
      // Response might be just the section HTML without full document structure
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = response;
      productElement = tempDiv.querySelector('[data-quick-add-product]');
    }
    
    // If still not found, try to find by section type
    if (!productElement) {
      productElement = doc.querySelector('[data-section-type="quick-add-product"]');
    }
    
    if (!productElement) {
      // Last resort: try to find any element with data-quick-add-item
      productElement = doc.querySelector('[data-quick-add-item]') || 
                      doc.querySelector('[data-quick-add-product]');
      
      // Or try parsing response as section HTML directly
      if (!productElement) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = response;
        productElement = tempDiv.querySelector('[data-quick-add-item]') || 
                        tempDiv.querySelector('[data-quick-add-product]');
      }
    }
    
    if (!productElement) {
      FCR_DEBUG.error('Product element not found in response');
      FCR_DEBUG.error('Response preview:', response.substring(0, 500));
      return null;
    }
    
    FCR_DEBUG.log('Product element found:', productElement);
    return productElement;
  } catch (error) {
    FCR_DEBUG.error('Error loading product content:', error);
    return null;
  }
}

// Render product content in modal
function renderProductContent(productElement) {
  // Ensure modal is cached
  if (!cachedElements.modal) {
    cachedElements.modal = document.querySelector('[data-quick-add-modal]');
  }
  
  if (!cachedElements.modal) {
    FCR_DEBUG.error('Modal element not found');
    return;
  }
  
  if (!cachedElements.modalContent) {
    cachedElements.modalContent = cachedElements.modal.querySelector('.quick-add-modal__content');
  }
  
  if (!cachedElements.modalContent) {
    FCR_DEBUG.error('Modal content container not found');
    return;
  }
  
  // Clear existing content
  cachedElements.modalContent.innerHTML = '';
  
  // Clone the product element to avoid issues
  const clonedElement = productElement.cloneNode(true);
  
  // Append product element
  cachedElements.modalContent.appendChild(clonedElement);
  
  FCR_DEBUG.log('Product content rendered in modal');
  
  // Clear cached elements to force re-initialization
  cachedElements.mainImage = null;
  cachedElements.variantContainer = null;
  cachedElements.variantJson = null;
}

// Initialize gallery and variant selection
function initializeGallery() {
  FCR_DEBUG.log('Initializing gallery...');
  
  const waitForElements = setInterval(() => {
    cachedElements.mainImage = document.querySelector('[data-quick-add-modal] [data-gallery-image]');
    cachedElements.variantContainer = document.querySelector('[data-quick-add-modal] [data-product-variants]');
    
    if (cachedElements.mainImage && cachedElements.variantContainer) {
      clearInterval(waitForElements);
      FCR_DEBUG.log('Elements found, proceeding with initialization');
      
      // Parse variant JSON - try multiple selectors
      let variantJsonElement = document.querySelector('[data-quick-add-modal] [data-variant-json]');
      if (!variantJsonElement) {
        variantJsonElement = document.querySelector('[data-quick-add-modal] script[type="application/json"][data-variant-json]');
      }
      if (!variantJsonElement) {
        variantJsonElement = document.querySelector('[data-quick-add-modal] script[data-variant-json]');
      }
      
      if (variantJsonElement) {
        try {
          cachedElements.variantJson = JSON.parse(variantJsonElement.textContent);
          FCR_DEBUG.log('Variant JSON parsed:', cachedElements.variantJson.length, 'variants');
          
          // Preselect variant if target is set
          if (targetVariantId) {
            setTimeout(() => {
              preselectVariant(targetVariantId);
            }, 200);
          } else if (targetColorValue) {
            // Fallback: find variant by color value
            setTimeout(() => {
              findVariantByColor(targetColorValue);
            }, 200);
          }
        } catch (e) {
          FCR_DEBUG.error('Error parsing variant JSON:', e);
        }
      } else {
        FCR_DEBUG.warn('Variant JSON element not found');
      }
      
      // Setup variant change listeners
      setupVariantListeners();
    }
  }, 100);
  
  // Clear interval after 5 seconds
  setTimeout(() => {
    clearInterval(waitForElements);
  }, 5000);
}

// Find variant by color value
function findVariantByColor(colorValue) {
  if (!cachedElements.variantJson || !colorValue) return;
  
  FCR_DEBUG.log('Finding variant by color value:', colorValue);
  
  // Find color option position
  let colorOptionPosition = null;
  const firstVariant = cachedElements.variantJson[0];
  if (firstVariant && firstVariant.options) {
    for (let i = 0; i < firstVariant.options.length; i++) {
      if (firstVariant.options[i] === colorValue) {
        colorOptionPosition = i;
        break;
      }
    }
  }
  
  // Find matching variants
  const matchingVariants = cachedElements.variantJson.filter(variant => {
    if (!variant.options || variant.options.length === 0) return false;
    if (colorOptionPosition !== null) {
      return variant.options[colorOptionPosition] === colorValue;
    }
    return variant.options.includes(colorValue);
  });
  
  if (matchingVariants.length > 0) {
    // Prioritize available variants
    const availableVariants = matchingVariants.filter(v => v.available);
    const selectedVariant = availableVariants.length > 0
      ? availableVariants[0]
      : matchingVariants[0];
    
    FCR_DEBUG.log('Found variant by color:', selectedVariant.id);
    preselectVariant(selectedVariant.id);
  } else {
    FCR_DEBUG.warn('No variant found with color value:', colorValue);
  }
}

// Preselect variant
function preselectVariant(variantId) {
  FCR_DEBUG.log('=== PRESELECT VARIANT START ===');
  FCR_DEBUG.log('Attempting to preselect variant:', variantId);
  
  if (!cachedElements.variantJson || !variantId) {
    FCR_DEBUG.error('Variant JSON or variant ID not available');
    return;
  }
  
  // Find target variant
  const variantIdStr = String(variantId);
  const targetVariant = cachedElements.variantJson.find(v => String(v.id) === variantIdStr);
  
  if (!targetVariant) {
    FCR_DEBUG.error('Target variant not found:', variantId);
    return;
  }
  
  FCR_DEBUG.log('Found target variant:', targetVariant);
  
  // Set all option values WITHOUT triggering change events
  const optionInputs = [];
  
  targetVariant.options.forEach((optionValue, index) => {
    FCR_DEBUG.log(`Setting option ${index + 1} to: ${optionValue}`);
    
    // Handle select dropdowns
    const selectInput = document.querySelector(`[data-quick-add-modal] [data-product-variants] select#option${index + 1}`);
    if (selectInput) {
      selectInput.value = optionValue;
      optionInputs.push(selectInput);
      FCR_DEBUG.log('Updated select input:', selectInput);
    }
    
    // Handle button-based options - try multiple selectors
    let targetButton = null;
    
    // Try exact match first
    targetButton = document.querySelector(`[data-quick-add-modal] [data-product-variants] button[data-option-value="${optionValue}"]`);
    
    // Try without quotes (in case value has special characters)
    if (!targetButton) {
      const allButtons = document.querySelectorAll(`[data-quick-add-modal] [data-product-variants] button[data-option-value]`);
      targetButton = Array.from(allButtons).find(btn => btn.getAttribute('data-option-value') === optionValue);
    }
    
    // Try aria-label match (for color swatches)
    if (!targetButton) {
      targetButton = document.querySelector(`[data-quick-add-modal] [data-product-variants] button[aria-label="${optionValue}"]`);
    }
    
    if (targetButton) {
      FCR_DEBUG.log('Found button for option:', optionValue, targetButton);
      
      const optionGroup = targetButton.closest('.product__option, [data-option-buttons], .product__color-swatch, [data-option-group]');
      if (optionGroup) {
        FCR_DEBUG.log('Found option group:', optionGroup);
        
        // Remove selected class from all buttons in group
        const allButtonsInGroup = optionGroup.querySelectorAll('button');
        allButtonsInGroup.forEach(btn => btn.classList.remove('selected', 'is-selected', 'active'));
        targetButton.classList.add('selected', 'is-selected', 'active');
        FCR_DEBUG.log('Updated button selection state');
        
        // Update swatch value display
        const swatchValueDisplay = optionGroup.querySelector('[data-swatch-selected], .product__color-swatch-value, [data-selected-value]');
        if (swatchValueDisplay) {
          swatchValueDisplay.textContent = optionValue;
          FCR_DEBUG.log('Updated swatch value display:', swatchValueDisplay);
        }
        
        // Update hidden select
        const hiddenSelect = optionGroup.querySelector('select');
        if (hiddenSelect) {
          hiddenSelect.value = optionValue;
          if (!optionInputs.includes(hiddenSelect)) {
            optionInputs.push(hiddenSelect);
          }
          FCR_DEBUG.log('Updated hidden select:', hiddenSelect);
        }
      } else {
        FCR_DEBUG.warn('Button found but no option group found for:', optionValue);
      }
    } else {
      FCR_DEBUG.log('No button found for option:', optionValue);
    }
  });
  
  // Now trigger change events for all inputs at once
  if (optionInputs.length > 0) {
    FCR_DEBUG.log('Triggering change events for', optionInputs.length, 'inputs');
    setTimeout(() => {
      optionInputs.forEach(input => {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }, 50);
  }
  
  // Update main image
  if (targetVariant.featured_image && cachedElements.mainImage) {
    let newImageSrc = targetVariant.featured_image.src;
    if (!newImageSrc.includes('_500x')) {
      newImageSrc = newImageSrc.replace(/(\.[^.]+)$/, '_500x$1');
    }
    
    const tempImage = new Image();
    tempImage.onload = function() {
      cachedElements.mainImage.src = newImageSrc;
      cachedElements.mainImage.alt = targetVariant.featured_image.alt || '';
    };
    tempImage.src = newImageSrc;
  }
  
  // Update variant container
  if (cachedElements.variantContainer) {
    cachedElements.variantContainer.setAttribute('data-current-variant-id', targetVariant.id);
  }
  
  FCR_DEBUG.log('=== PRESELECT VARIANT END ===');
}

// Setup variant change listeners
function setupVariantListeners() {
  const variantInputs = document.querySelectorAll('[data-quick-add-modal] [data-product-variants] select, [data-quick-add-modal] [data-product-variants] button[data-option-value]');
  
  if (variantInputs.length === 0) {
    FCR_DEBUG.log('No variant inputs found');
    return;
  }
  
  // Handle select changes
  variantInputs.forEach(input => {
    if (input.tagName === 'SELECT') {
      input.addEventListener('change', function() {
        updateVariantImage();
      });
    }
  });
  
  // Handle button clicks
  document.querySelectorAll('[data-quick-add-modal] [data-product-variants] button[data-option-value]').forEach(button => {
    button.addEventListener('click', function() {
      const optionGroup = this.closest('.product__option, [data-option-buttons]');
      if (optionGroup) {
        optionGroup.querySelectorAll('button').forEach(btn => btn.classList.remove('selected'));
        this.classList.add('selected');
        
        const hiddenSelect = optionGroup.querySelector('select');
        if (hiddenSelect) {
          hiddenSelect.value = this.getAttribute('data-option-value');
          hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  });
}

// Update variant image
function updateVariantImage() {
  if (!cachedElements.variantJson || !cachedElements.mainImage) return;
  
  // Get current selected options
  const selectedOptions = {};
  document.querySelectorAll('[data-quick-add-modal] [data-product-variants] select').forEach(select => {
    const name = select.name.replace('options[', '').replace(']', '');
    selectedOptions[name] = select.value;
  });
  
  // Find matching variant
  const matchingVariant = cachedElements.variantJson.find(variant => {
    return variant.options.every((option, index) => {
      const optionName = Object.keys(selectedOptions)[index];
      return variant.options[index] === selectedOptions[optionName];
    });
  });
  
  if (matchingVariant && matchingVariant.featured_image) {
    let newImageSrc = matchingVariant.featured_image.src;
    if (!newImageSrc.includes('_500x')) {
      newImageSrc = newImageSrc.replace(/(\.[^.]+)$/, '_500x$1');
    }
    
    const tempImage = new Image();
    tempImage.onload = function() {
      cachedElements.mainImage.src = newImageSrc;
      cachedElements.mainImage.alt = matchingVariant.featured_image.alt || '';
    };
    tempImage.src = newImageSrc;
  }
}

// Handle button click
async function handleButtonClick(e, button) {
  try {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    FCR_DEBUG.log('=== FCR BUTTON CLICK HANDLED ===');
    FCR_DEBUG.log('Button:', button);
    FCR_DEBUG.log('Button attributes:', {
      'data-fcr-quick-add': button.getAttribute('data-fcr-quick-add'),
      'data-quick-add': button.getAttribute('data-quick-add'),
      'data-fcr-variant-id': button.getAttribute('data-fcr-variant-id'),
      'data-variant-id': button.getAttribute('data-variant-id'),
      'data-fcr-color-value': button.getAttribute('data-fcr-color-value'),
      'data-color-value': button.getAttribute('data-color-value')
    });
    
    // Store target info - try multiple attribute names
    targetProductHandle = button.dataset.fcrQuickAdd || 
                         button.getAttribute('data-fcr-quick-add') ||
                         button.dataset.quickAdd || 
                         button.getAttribute('data-quick-add');
    targetVariantId = button.dataset.fcrVariantId || 
                     button.getAttribute('data-fcr-variant-id') ||
                     button.dataset.variantId || 
                     button.getAttribute('data-variant-id');
    targetColorValue = button.dataset.fcrColorValue || 
                      button.getAttribute('data-fcr-color-value') ||
                      button.dataset.colorValue || 
                      button.getAttribute('data-color-value');
    
    FCR_DEBUG.log('Extracted target info:', {
      productHandle: targetProductHandle,
      variantId: targetVariantId,
      colorValue: targetColorValue
    });
    
    if (!targetProductHandle) {
      FCR_DEBUG.error('Product handle not found');
      FCR_DEBUG.error('Button dataset:', button.dataset);
      FCR_DEBUG.error('Button attributes:', Array.from(button.attributes).map(attr => `${attr.name}="${attr.value}"`));
      return;
    }
    
    // Ensure modal is available
    if (!cachedElements.modal) {
      cachedElements.modal = document.querySelector('[data-quick-add-modal]');
      if (!cachedElements.modal) {
        FCR_DEBUG.error('Modal element not found');
        return;
      }
    }
    
    // Load product content
    FCR_DEBUG.log('Loading product content for handle:', targetProductHandle);
    const productElement = await loadProductContent(targetProductHandle);
    if (!productElement) {
      FCR_DEBUG.error('Failed to load product content');
      return;
    }
    
    // Render content in modal
    renderProductContent(productElement);
    
    // Wait a bit for DOM to update after rendering content
    // Then open modal and initialize gallery
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Open modal after content is rendered
        openModal();
        
        // Initialize gallery after modal is opened
        setTimeout(() => {
          initializeGallery();
        }, 100);
      }, 50); // Small delay to ensure DOM is updated
    });
  } catch (error) {
    FCR_DEBUG.error('Error in handleButtonClick:', error);
    FCR_DEBUG.error('Error stack:', error.stack);
  }
}

// Check if we're on a product page
function isProductPage() {
  // Check multiple indicators
  const bodyClass = document.body ? document.body.classList.contains('template-product') : false;
  const pathname = window.location.pathname.includes('/products/');
  const result = bodyClass || pathname;
  
  FCR_DEBUG.log('isProductPage check:', {
    bodyClass: bodyClass,
    bodyClassName: document.body ? document.body.className : 'body not available',
    pathname: window.location.pathname,
    pathnameMatch: pathname,
    result: result
  });
  
  return result;
}

// Setup event listeners
function setupEventListeners() {
  FCR_DEBUG.log('=== setupEventListeners() called ===');
  
  // Check if we're on a product page first
  const isProduct = isProductPage();
  FCR_DEBUG.log('isProductPage() result:', isProduct);
  
  if (!isProduct) {
    FCR_DEBUG.log('Not on product page, skipping FCR quick add setup');
    FCR_DEBUG.log('Body classes:', document.body ? document.body.className : 'body not available');
    FCR_DEBUG.log('Pathname:', window.location.pathname);
    return;
  }
  
  // Prevent multiple setups
  if (eventListenersSetup) {
    FCR_DEBUG.log('Event listeners already setup, skipping');
    return;
  }
  
  FCR_DEBUG.log('✅ Setting up FCR quick add listeners on product page');
  eventListenersSetup = true;
  
  // Initialize modal element
  cachedElements.modal = document.querySelector('[data-quick-add-modal]');
  if (cachedElements.modal) {
    FCR_DEBUG.log('Modal element found:', cachedElements.modal);
  } else {
    FCR_DEBUG.warn('Modal element not found, will retry later');
    FCR_DEBUG.warn('Searched for: [data-quick-add-modal]');
    FCR_DEBUG.warn('Available modals:', document.querySelectorAll('[data-quick-add-modal]').length);
  }
  
  // Check for FCR sections on the page
  const fcrSections = document.querySelectorAll('[data-fcr-section="true"]');
  FCR_DEBUG.log('FCR sections found on page:', fcrSections.length);
  
  // Check for FCR buttons
  const fcrButtons = document.querySelectorAll('[data-fcr-section="true"] [data-quick-add]');
  FCR_DEBUG.log('FCR quick-add buttons found:', fcrButtons.length);
  
  // Listen for button clicks in capture phase (before theme.js)
  // Use document-level delegation to catch dynamically added buttons
  document.addEventListener('click', function(e) {
    FCR_DEBUG.log('=== Document click detected ===');
    FCR_DEBUG.log('Click target:', e.target);
    FCR_DEBUG.log('Click currentTarget:', e.currentTarget);
    
    // Check if click is on a quick-add button - try multiple selectors
    let button = e.target.closest('[data-quick-add][data-open-modal]');
    
    // If not found, try without data-open-modal requirement
    if (!button) {
      button = e.target.closest('[data-quick-add]');
    }
    
    // Try to find button with FCR attributes
    if (!button) {
      button = e.target.closest('[data-fcr-quick-add]');
    }
    
    if (!button) {
      FCR_DEBUG.log('Not a quick-add button, ignoring');
      return; // Not a quick-add button, ignore
    }
    
    FCR_DEBUG.log('✅ Quick-add button found:', button);
    
    // Check if button is inside featured-collection-row section
    const fcrSection = button.closest('[data-fcr-section="true"]');
    const isInFCR = fcrSection !== null;
    
    FCR_DEBUG.log('Button attributes:', {
      'data-quick-add': button.getAttribute('data-quick-add'),
      'data-fcr-quick-add': button.getAttribute('data-fcr-quick-add'),
      'data-variant-id': button.getAttribute('data-variant-id'),
      'data-fcr-variant-id': button.getAttribute('data-fcr-variant-id'),
      'data-color-value': button.getAttribute('data-color-value'),
      'data-fcr-color-value': button.getAttribute('data-fcr-color-value'),
      'data-open-modal': button.getAttribute('data-open-modal')
    });
    FCR_DEBUG.log('Is in FCR section:', isInFCR);
    FCR_DEBUG.log('FCR section element:', fcrSection);
    FCR_DEBUG.log('Is product page:', isProductPage());
    
    // Only handle if we're on product page AND button is in FCR
    if (isInFCR && isProductPage()) {
      FCR_DEBUG.log('✅✅✅ FCR button click detected on product page, intercepting...');
      handleButtonClick(e, button);
    } else {
      FCR_DEBUG.log('Button click not handled:', {
        isInFCR: isInFCR,
        isProductPage: isProductPage(),
        reason: !isInFCR ? 'Not in FCR section' : 'Not on product page'
      });
    }
  }, true); // Capture phase - MUST be before theme.js
  
  // Listen for modal close button
  document.addEventListener('click', function(e) {
    const closeBtn = e.target.closest('[data-quick-add-modal-close]');
    const wash = e.target.closest('.quick-add-modal__wash');
    
    if ((closeBtn || wash) && cachedElements.modal) {
      e.preventDefault();
      closeModal();
    }
  }, true);
  
  // Listen for ESC key
  document.addEventListener('keydown', function(e) {
    if (e.keyCode === 27 && cachedElements.modal && cachedElements.modal.classList.contains('is-visible')) {
      closeModal();
    }
  });
  
  FCR_DEBUG.log('Event listeners setup complete');
}

// Initialize - try multiple times to ensure DOM is ready
function initialize() {
  FCR_DEBUG.log('initialize() called');
  FCR_DEBUG.log('document.readyState:', document.readyState);
  FCR_DEBUG.log('document.body:', document.body);
  
  // Wait a bit for DOM to be fully ready (body might not be available yet)
  setTimeout(() => {
    FCR_DEBUG.log('Checking if on product page...');
    const isProduct = isProductPage();
    
    if (!isProduct) {
      FCR_DEBUG.log('Not on product page, skipping initialization');
      return;
    }
    
    FCR_DEBUG.log('On product page, setting up event listeners...');
    setupEventListeners();
    
    // Also check if modal exists, if not, retry
    if (!cachedElements.modal) {
      setTimeout(() => {
        cachedElements.modal = document.querySelector('[data-quick-add-modal]');
        if (cachedElements.modal) {
          FCR_DEBUG.log('Modal element found on retry');
        } else {
          FCR_DEBUG.warn('Modal element still not found after retry');
        }
      }, 500);
    }
  }, 100);
}

// Initialize immediately - script might load after DOM is ready
FCR_DEBUG.log('=== Script execution started ===');
FCR_DEBUG.log('document.readyState:', document.readyState);
FCR_DEBUG.log('document.body exists:', !!document.body);
FCR_DEBUG.log('window.location.pathname:', window.location.pathname);

// Initialize on DOM ready
if (document.readyState === 'loading') {
  FCR_DEBUG.log('Document still loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', function() {
    FCR_DEBUG.log('✅ DOMContentLoaded fired');
    initialize();
  });
} else {
  FCR_DEBUG.log('Document already loaded, initializing immediately');
  initialize();
}

// Also try after a short delay to catch late-loaded content
setTimeout(function() {
  FCR_DEBUG.log('=== Delayed initialization attempt (500ms) ===');
  initialize();
}, 500);

// One more attempt after a longer delay
setTimeout(function() {
  FCR_DEBUG.log('=== Final initialization attempt (1000ms) ===');
  initialize();
}, 1000);

// Extra attempt after 2 seconds for very slow pages
setTimeout(function() {
  FCR_DEBUG.log('=== Extra initialization attempt (2000ms) ===');
  initialize();
}, 2000);

// Export debug controls
window.fcrQuickAddDebug = {
  enable: function() {
    FCR_DEBUG.enabled = true;
    console.log('[FCR Quick Add] Debug mode enabled');
  },
  disable: function() {
    FCR_DEBUG.enabled = false;
    console.log('[FCR Quick Add] Debug mode disabled');
  }
};

FCR_DEBUG.log('=== Script loaded and initialized ===');
FCR_DEBUG.log('Current page check:', {
  bodyClass: document.body ? document.body.className : 'body not available',
  pathname: window.location.pathname,
  isProductPage: document.body ? (document.body.classList.contains('template-product') || window.location.pathname.includes('/products/')) : false
});

// Final check - log script status
console.log('[FCR Quick Add] 🔍 Script status check:', {
  scriptLoaded: true,
  FCR_DEBUGEnabled: FCR_DEBUG.enabled,
  eventListenersSetup: eventListenersSetup,
  documentReady: document.readyState,
  isProductPage: document.body ? (document.body.classList.contains('template-product') || window.location.pathname.includes('/products/')) : false
});

